import React, { useEffect, useState } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import { Box, Button, Container, Paper, Typography } from '@mui/material';
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getAuth, onAuthStateChanged } from 'firebase/auth';

const STRINGS = {
  en: {
    typeLabels: {
      notifyFutureEvents: 'event invitations',
      sendRunningOffers: 'running offers and newsletters',
      default: 'emails',
    },
    ok: {
      title: 'You have been unsubscribed',
      body: (label: string) => `You will no longer receive ${label} from us.`,
      manage: 'You can manage all your communication preferences on My Page.',
      button: 'Go to My Page',
    },
    invalid: {
      title: 'Link not valid',
      body: 'This unsubscribe link is invalid or has already been used.',
      manage: 'You can manage your communication preferences on My Page.',
      button: 'Go to My Page',
    },
    error: {
      title: 'Something went wrong',
      body: 'We could not process your request. Please try again or contact support.',
      button: 'Back to home',
    },
  },
  no: {
    typeLabels: {
      notifyFutureEvents: 'arrangementsinvitasjoner',
      sendRunningOffers: 'løpstilbud og nyhetsbrev',
      default: 'e-poster',
    },
    ok: {
      title: 'Du er avmeldt',
      body: (label: string) => `Du vil ikke lenger motta ${label} fra oss.`,
      manage: 'Du kan administrere alle kommunikasjonspreferansene dine på Min Side.',
      button: 'Gå til Min Side',
    },
    invalid: {
      title: 'Koblingen er ikke gyldig',
      body: 'Denne avmeldingskoblingen er ugyldig eller har allerede blitt brukt.',
      manage: 'Du kan administrere kommunikasjonspreferansene dine på Min Side.',
      button: 'Gå til Min Side',
    },
    error: {
      title: 'Noe gikk galt',
      body: 'Vi kunne ikke behandle forespørselen din. Prøv igjen eller kontakt support.',
      button: 'Tilbake til hjemmesiden',
    },
  },
} as const;

type Lang = keyof typeof STRINGS;

function normalizeLang(lang: string): Lang {
  return lang.startsWith('no') || lang.startsWith('nb') ? 'no' : 'en';
}

const UnsubscribedPage: React.FC = () => {
  const [params] = useSearchParams();
  const { i18n } = useTranslation();
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);

  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (user) => setIsLoggedIn(!!user));
    return unsub;
  }, []);

  const status = params.get('status') || 'ok';
  const type = params.get('type') || '';
  const urlLang = params.get('lang') || '';

  // Logged-in users: use current UI language. Others: use URL lang param, fall back to UI lang.
  const uiLang = normalizeLang(i18n.language || 'en');
  const effectiveLang: Lang = isLoggedIn === true ? uiLang : (urlLang ? normalizeLang(urlLang) : uiLang);
  const s = STRINGS[effectiveLang];

  const typeLabel = s.typeLabels[type as keyof typeof s.typeLabels] ?? s.typeLabels.default;

  if (status === 'ok') {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Box sx={{ mb: 3 }}>
            <CheckCircle size={64} color="#4caf50" />
          </Box>
          <Typography variant="h4" gutterBottom>
            {s.ok.title}
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            {s.ok.body(typeLabel)}
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            {s.ok.manage}
          </Typography>
          <Button
            component={RouterLink}
            to="/my-registrations"
            variant="contained"
            sx={{ mt: 1 }}
          >
            {s.ok.button}
          </Button>
        </Paper>
      </Container>
    );
  }

  if (status === 'notfound' || status === 'invalid') {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Box sx={{ mb: 3 }}>
            <XCircle size={64} color="#f44336" />
          </Box>
          <Typography variant="h4" gutterBottom>
            {s.invalid.title}
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            {s.invalid.body}
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            {s.invalid.manage}
          </Typography>
          <Button
            component={RouterLink}
            to="/my-registrations"
            variant="contained"
            sx={{ mt: 1 }}
          >
            {s.invalid.button}
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Box sx={{ mb: 3 }}>
          <AlertCircle size={64} color="#ff9800" />
        </Box>
        <Typography variant="h4" gutterBottom>
          {s.error.title}
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          {s.error.body}
        </Typography>
        <Button
          component={RouterLink}
          to="/"
          variant="outlined"
          sx={{ mt: 1 }}
        >
          {s.error.button}
        </Button>
      </Paper>
    </Container>
  );
};

export default UnsubscribedPage;
